import type { Account, PortalRead } from "@baton/api";
import { sidebarTicketCountriesKey } from "@baton/core";
import { messageKey, useI18n } from "@baton/i18n";
import { Ticket } from "@baton/icons";
import { ErrorState } from "@baton/ui";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router";
import { accountsQuery, NO_ACCOUNT_FILTERS } from "../../accounts/list/query.ts";
import { problemOf } from "../../app/problems.ts";
import { useRuntime } from "../../app/RuntimeProvider.tsx";
import { ticketsPath } from "../../portal/paths.ts";
import { portalsQuery } from "../../portal/queries.ts";
import type { ModeDestination, ModeSidebarProps } from "../modes/modes.tsx";
import { GlideList } from "./GlideList.tsx";
import { EntriesPlaceholder } from "./placeholders.tsx";
import { RegionsSection } from "./RegionsSection.tsx";
import { SIDEBAR_PILL, SidebarItem, SidebarSection } from "./Sidebar.tsx";

/**
 * 028 FR-028-047, `specs/015-web-frontend/contracts/screens.md` § 028 Client portal, the Tickets mode
 * as a portal worker sees it (the approved canvas's board 8, 2026-10-20): at the top the mode's one
 * screen, Tickets, every client's tickets with Follow; under it, after the sidebar's section gap, the
 * clients that have tickets, open and closed, under REGIONS as the Clients mode lists clients
 * (amended 2026-10-21, by the owner's decision): each country a collapsible heading with its flag and
 * the count of its clients, the clients located by a region alone under their area's heading, the
 * section the Clients mode draws (`RegionsSection.tsx`), so two clients of one name in different
 * countries are never read as one. A closed portal's row carries the closed mark at its end
 * (FR-028-047), and each row counts the client's open tickets and opens the client's tickets page;
 * since 2026-10-24 each country's or area's heading counts its clients' open tickets and links to the
 * board of that place, and All tickets says how many are open over every client (FR-028-071,
 * FR-028-076, the approved Main board), the reader's unread messages being on each ticket's card. The rows are the Tickets screen's own read of the
 * portals, every page of it, placed by the client list's own unfiltered read, every page of it, which
 * is the Clients mode's cache entry; the section is absent while the Team has no portal. A portal
 * whose client that read does not hold is not dropped: it is listed by its client's name under its own
 * heading after the regions, since it cannot be placed.
 */
export const TICKETS_DESTINATIONS: readonly ModeDestination[] = [{ id: "tickets", label: messageKey("nav.tickets.all"), path: "tickets", area: "clients", areaLevel: "portal" }];

export function TicketsSidebar({ ws, onNavigate }: ModeSidebarProps) {
  const { t } = useI18n();
  const runtime = useRuntime();
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const portals = useEveryPage(useInfiniteQuery(portalsQuery(runtime, ws)));
  const accounts = useEveryPage(useInfiniteQuery(accountsQuery(runtime, ws, NO_ACCOUNT_FILTERS)));
  const byAccount = useMemo(() => new Map((portals.data?.pages ?? []).flatMap((page) => page.items).map((portal) => [portal.account.id, portal])), [portals.data]);
  const clients = useMemo(() => (accounts.data?.pages ?? []).flatMap((page) => page.items).filter((account) => byAccount.has(account.id)), [accounts.data, byAccount]);
  const unplaced = useMemo(() => {
    const placed = new Set(clients.map((client) => client.id));
    return [...byAccount.values()].filter((portal) => !placed.has(portal.account.id)).sort((a, b) => a.account.name.localeCompare(b.account.name));
  }, [byAccount, clients]);
  const navigateProps = onNavigate === undefined ? {} : { onNavigate };
  // A portal worker reads the Team shape, which counts the portal's open tickets (FR-028-076).
  const openOf = (portal: PortalRead | undefined) => (portal !== undefined && "open_request_count" in portal ? portal.open_request_count : 0);
  const portalRow = (portal: PortalRead, nested: boolean) => {
    const to = ticketsPath(ws, portal.account.id);
    const name = portal.account.name;
    return (
      <SidebarItem
        key={portal.account.id}
        to={to}
        label={name}
        current={pathname === to || pathname.startsWith(`${to}/`)}
        nested={nested}
        count={openOf(portal)}
        countLabel={t("nav.tickets.open", { count: openOf(portal), client: name })}
        {...(portal.status === "closed" ? { tag: { text: t("nav.tickets.closed"), label: t("nav.tickets.closedLabel", { client: name }), word: true } } : {})}
        {...navigateProps}
      />
    );
  };
  const clientRow = (client: Account) => {
    const portal = byAccount.get(client.id);
    return portal === undefined ? null : portalRow(portal, true);
  };
  // A later page that failed leaves the earlier pages' data and a next page still owed, so a failure of
  // any page is the read's failure, with its Retry, rather than a placeholder that never resolves.
  const failed = [portals, accounts].find((read) => read.isError);
  const totalOpen = [...byAccount.values()].reduce((total, portal) => total + openOf(portal), 0);
  const onTickets = pathname === `/${ws}/tickets`;
  return (
    <>
      <SidebarSection id="tickets-overview" title={t("nav.tickets.section.overview")}>
        <GlideList className="b-sidebar__list" pillClassName={SIDEBAR_PILL} data-sidebar-destinations>
          <SidebarItem
            to={`/${ws}/tickets`}
            label={t("nav.tickets.all")}
            icon={<Ticket />}
            current={onTickets && params.get("country") === null && params.get("region") === null}
            {...(portals.data === undefined ? {} : { tag: { text: t("nav.tickets.openShort", { count: totalOpen }), label: t("nav.tickets.openTotal", { count: totalOpen }), word: true } })}
            {...navigateProps}
          />
        </GlideList>
      </SidebarSection>
      {failed !== undefined ? (
        <div className="b-sidebar__section">
          <ErrorState form="inline" problem={problemOf(failed.error)} onRetry={() => void failed.refetch()} />
        </div>
      ) : portals.isPending || portals.hasNextPage || accounts.isPending || accounts.hasNextPage ? (
        <EntriesPlaceholder />
      ) : (
        <>
          <RegionsSection
            id="tickets-regions"
            ws={ws}
            clients={clients}
            collapsedKey={sidebarTicketCountriesKey}
            row={clientRow}
            headingCount={(placed) => {
              const count = placed.reduce((total, client) => total + openOf(byAccount.get(client.id)), 0);
              return { count, label: t("nav.tickets.openTotal", { count }) };
            }}
            headingLink={(place) => {
              const query = place.country !== null ? `country=${place.country}` : `region=${place.areaCode ?? ""}`;
              const current = onTickets && (place.country !== null ? params.get("country") === place.country : params.get("region") === place.areaCode);
              return { to: `/${ws}/tickets?${query}`, current, toggleLabel: t("nav.tickets.placeToggle", { place: place.name }), ...navigateProps };
            }}
          />
          {unplaced.length > 0 && (
            <SidebarSection id="tickets-unplaced" title={t("nav.tickets.section.unplaced")}>
              <GlideList className="b-sidebar__list" pillClassName={SIDEBAR_PILL}>
                {unplaced.map((portal) => portalRow(portal, false))}
              </GlideList>
            </SidebarSection>
          )}
        </>
      )}
    </>
  );
}

/** Read every page of a list, so the sidebar places every row; the ticket board places its cards' clients by the same read. */
export function useEveryPage<T extends { readonly hasNextPage: boolean; readonly isFetchingNextPage: boolean; readonly isError: boolean; readonly fetchNextPage: () => Promise<unknown> }>(read: T): T {
  const { hasNextPage, isFetchingNextPage, isError, fetchNextPage } = read;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !isError) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, isError, fetchNextPage]);
  return read;
}
